import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from "react";
import { Icon } from "./components/Icon";
import { resolveHelpLead } from "./helpLead";
import { LanguageButton } from "./components/LanguageButton";
import { Num } from "./components/Num";
import { hydrateRsvp, submitCheckIn } from "./api/meetingKioskApi";
import { ApiResponseError } from "../../shared/services/ApiResponseError";
import { applyMeetingCapacity } from "./applyMeetingCapacity";
import { registrationClosedMessage } from "./registrationClosedMessage";
import { submitRefusalMessage } from "./submitRefusalMessage";
import type { SubmitRefusal } from "./submitRefusalMessage";
import { Lookup } from "./steps/Lookup";
import { Welcome } from "./steps/Welcome";
import { YourInfo } from "./steps/YourInfo";
import { Picker } from "./steps/Picker";
import { Review } from "./steps/Review";
import { Done } from "./steps/Done";
import { useClock } from "./hooks/useClock";
import { useGoogleTranslate } from "./hooks/useGoogleTranslate";
import { useKioskData } from "./hooks/useKioskData";
import { useStatusPoll } from "./hooks/useStatusPoll";
import { computeKioskSteps } from "./computeKioskSteps";
import type { KioskStepDef, KioskStepKey } from "./computeKioskSteps";
import { reconcileStepIndex, resolveStepIndex } from "./reconcileStepIndex";
import { validateInfoStep } from "./validateInfoStep";
import { resolveRegistrationFields } from "./registrationFields";
import {
  normalizeShoutyCaps,
  applyTranslateLanguage,
  retranslateCurrentLanguage,
  RETRANSLATE_DEBOUNCE_MS,
  TRANSLATE_PROBE_TEXT,
  TRANSLATE_UNAVAILABLE_NOTICE
} from "./utils";
import type {
  AgendaSection,
  DroppedAgendaItem,
  FormData,
  KioskLanguage,
  KioskMeetingStatus,
  LookupResult,
  MeetingKioskBootstrap,
  Stance
} from "./types";

type StepKey = KioskStepKey;

const EMPTY_SECTIONS: AgendaSection[] = [];

// AB#9994 — see helpLead.ts for why the resolution rules live in their own module.

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

interface AppProps {
  bootstrap: MeetingKioskBootstrap;
}

export const App: React.FC<AppProps> = ({ bootstrap }) => {
  // --- data, clock, polling hooks ------------------------------------------
  const { data, setData, loadError } = useKioskData(bootstrap.dataUrl);
  const clock = useClock();

  const [stepIdx, setStepIdx] = useState(0);
  const [form, setForm] = useState<FormData>({
    first: "",
    last: "",
    email: "",
    phone: "",
    address: "",
    zip: "",
    userIdString: "",
    attend: "inperson",
    smsConsent: false,
    customFields: {},
    interpreterRequested: false,
    interpreterLanguage: "",
    editToken: ""
  });
  const [selections, setSelections] = useState<Record<number, Stance>>({});
  // Bug #10369: the ids this person explicitly deselected on the Picker. Only the Picker's tap
  // handlers write here, so a withdrawal is always something the resident stated - it is never
  // inferred from `selections` going empty, which also happens when the picker was never
  // populated or when the status poll pruned an item that closed underneath them.
  const [removedItemIds, setRemovedItemIds] = useState<number[]>([]);
  const [closedNotice, setClosedNotice] = useState<string | null>(null);
  const [subscribe, setSubscribe] = useState(false);
  const [languageCode, setLanguageCode] = useState("en");
  const [done, setDone] = useState(false);
  const [droppedItems, setDroppedItems] = useState<DroppedAgendaItem[]>([]);
  const [submitRefusal, setSubmitRefusal] = useState<SubmitRefusal | null>(
    null
  );
  const [submitting, setSubmitting] = useState(false);
  const [capacityRefresh, setCapacityRefresh] = useState(0);

  const kioskLanguages = useMemo<KioskLanguage[]>(() => {
    const configured = data?.inPersonSupportedLanguages ?? [];
    const withEnglish: KioskLanguage[] = [
      { code: "en", name: "English", autoglossonym: "English" },
      ...configured
    ];
    const seen = new Set<string>();
    return withEnglish.filter((l) => {
      const key = l.code.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [data?.inPersonSupportedLanguages]);

  const [lookupMode, setLookupMode] = useState(false);
  // AB#9875: with Participation Check-In off the lookup screen must be unreachable, not just
  // hidden behind a missing button.
  const participationCheckInEnabled =
    data?.participationCheckInEnabled === true;
  const showLookupScreen = lookupMode && participationCheckInEnabled;
  const [returningUser, setReturningUser] = useState(false);
  const [alreadyHoldsSlots, setAlreadyHoldsSlots] = useState(false);
  const [hydrateError, setHydrateError] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showLangs, setShowLangs] = useState(false);
  const [showSmsInfo, setShowSmsInfo] = useState(false);

  const userPickedLanguageRef = useRef(false);

  // --- Google Translate init -----------------------------------------------
  useGoogleTranslate(kioskLanguages);

  // --- language code ref (for async handlers) ------------------------------
  const languageCodeRef = useRef(languageCode);
  const setLanguage = useCallback((code: string) => {
    languageCodeRef.current = code;
    setLanguageCode(code);
  }, []);

  // --- cookie seed (apply pre-existing googtrans on first load) ------------
  const cookieSeedDoneRef = useRef(false);
  useEffect(() => {
    if (!data || cookieSeedDoneRef.current) return;
    if (kioskLanguages.length <= 1) return;
    cookieSeedDoneRef.current = true;
    const targets = googTransCookieTargets();
    if (targets.length === 0) return;
    const validated = matchKioskLanguage(targets[0], kioskLanguages);
    if (validated && validated !== "en") {
      setLanguage(validated);
      applyTranslateLanguage(validated);
    }
  }, [data, kioskLanguages]);

  // --- re-translate whatever React just rendered ---------------------------
  useEffect(() => {
    const t = window.setTimeout(
      () => retranslateCurrentLanguage(languageCodeRef.current),
      RETRANSLATE_DEBOUNCE_MS
    );
    return () => window.clearTimeout(t);
  });

  // --- status poll ---------------------------------------------------------
  // --- step computation ----------------------------------------------------
  const openAgendaItemCount = useMemo(
    () =>
      (data?.agendaItems ?? []).filter((it) => it.acceptingSpeakers !== false)
        .length,
    [data?.agendaItems]
  );

  const buildSteps = useCallback(
    (session: {
      alreadyHoldsSlots: boolean;
      returningUser: boolean;
      lookingUp: boolean;
    }) =>
      computeKioskSteps({
        speakersCanRegister: data?.meeting?.speakersCanRegister,
        openAgendaItemCount,
        ...session
      }),
    [data?.meeting?.speakersCanRegister, openAgendaItemCount]
  );

  const steps = useMemo(
    () =>
      buildSteps({
        alreadyHoldsSlots,
        returningUser,
        lookingUp: showLookupScreen
      }),
    [buildSteps, alreadyHoldsSlots, returningUser, showLookupScreen]
  );
  const meetingSteps = useMemo(
    () =>
      buildSteps({
        alreadyHoldsSlots: false,
        returningUser: false,
        lookingUp: false
      }),
    [buildSteps]
  );
  const hasPickStep = steps.some((s) => s.key === "pick");

  // The step list is derived from live polled state, so it can shrink underfoot. stepIdx is a
  // raw index, so it has to be remapped rather than left pointing at a different screen.
  const previousStepKeysRef = useRef<StepKey[]>(steps.map((s) => s.key));
  useEffect(() => {
    const nextStepKeys = steps.map((s) => s.key);
    const previousStepKeys = previousStepKeysRef.current;

    if (previousStepKeys.join() !== nextStepKeys.join()) {
      setStepIdx((current) =>
        reconcileStepIndex(current, previousStepKeys, nextStepKeys)
      );
      previousStepKeysRef.current = nextStepKeys;
    }
  }, [steps]);

  const screen: StepKey = steps[stepIdx]?.key ?? "welcome";

  const registrationUnavailableReason =
    data?.meeting?.registrationUnavailableReason;
  useEffect(() => {
    if (screen === "welcome" || done || returningUser) return;
    const message = registrationClosedMessage(registrationUnavailableReason);
    if (message) setClosedNotice(message);
  }, [registrationUnavailableReason, screen, done, returningUser]);

  useStatusPoll({
    statusUrl: bootstrap.statusUrl,
    data,
    setData,
    selections,
    setSelections,
    done,
    setClosedNotice,
    refreshKey: `${screen}:${capacityRefresh}`
  });

  const applyServerCapacity = useCallback(
    (status: KioskMeetingStatus | null | undefined) => {
      if (!status) return;
      setData((current) => {
        if (!current?.meeting) return current;
        const meeting = applyMeetingCapacity(current.meeting, status);
        return meeting === current.meeting ? current : { ...current, meeting };
      });
    },
    [setData]
  );

  const selectLanguage = (code: string) => {
    userPickedLanguageRef.current = true;
    const languageStillApplied = languageCodeRef.current;
    setLanguage(code);
    applyTranslateLanguage(code).then((outcome) => {
      if (outcome !== "unavailable") return;
      setLanguage(languageStillApplied);
      setClosedNotice(TRANSLATE_UNAVAILABLE_NOTICE);
    });
  };

  const goToStep = (key: StepKey, flow: KioskStepDef[] = steps) => {
    const nextStepKeys = flow.map((s) => s.key);
    const previousStepKeys = previousStepKeysRef.current;
    previousStepKeysRef.current = nextStepKeys;
    setStepIdx((current) =>
      resolveStepIndex(key, current, previousStepKeys, nextStepKeys)
    );
  };

  const nameMode = data?.nameMode ?? "split";
  const registrationFields = resolveRegistrationFields(
    data?.registrationFields,
    nameMode,
    data?.customFields ?? []
  );

  const infoValidation = validateInfoStep({
    fields: registrationFields,
    values: {
      first: form.first,
      last: form.last,
      email: form.email,
      phone: form.phone,
      address: form.address,
      zip: form.zip,
      customFields: form.customFields
    },
    attendingVirtually: form.attend === "virtual",
    interpreterOffered: data?.inPersonTranslationServices ?? false,
    interpreterRequested: form.interpreterRequested,
    interpreterLanguage: form.interpreterLanguage
  });
  const canAdvance = screen !== "info" || infoValidation.canAdvance;

  const isFinalStep = stepIdx === steps.length - 1;

  const next = () => {
    if (stepIdx < steps.length - 1) {
      setStepIdx(stepIdx + 1);
      return;
    }
    submit();
  };
  const back = () => stepIdx > 0 && setStepIdx(stepIdx - 1);

  const resetSession = (targetStep: StepKey = "welcome") => {
    setForm({
      first: "",
      last: "",
      email: "",
      phone: "",
      address: "",
      zip: "",
      userIdString: "",
      attend: "inperson",
      smsConsent: false,
      customFields: {},
      interpreterRequested: false,
      interpreterLanguage: "",
      editToken: ""
    });
    setSelections({});
    setRemovedItemIds([]);
    setClosedNotice(null);
    setSubscribe(false);
    goToStep(targetStep, meetingSteps);
    setDone(false);
    setSubmitRefusal(null);
    setLookupMode(false);
    setReturningUser(false);
    setAlreadyHoldsSlots(false);
    setHydrateError(null);
    setDroppedItems([]);
    setShowHelp(false);
    setShowLangs(false);
    setShowSmsInfo(false);
    if (targetStep === "welcome") {
      setLanguage("en");
      applyTranslateLanguage("en");
      userPickedLanguageRef.current = false;
    }
  };

  // Bug #10369: the only path that records a withdrawal. useStatusPoll's pruning and the
  // post-submit droppedItems cleanup call setSelections directly and deliberately bypass this,
  // so an item the clerk closed can never be reported as something the resident gave up.
  const onUserToggledItem = useCallback((id: number, nowSelected: boolean) => {
    setRemovedItemIds((ids) => {
      if (nowSelected) return ids.filter((x) => x !== id);
      return ids.includes(id) ? ids : [...ids, id];
    });
  }, []);

  const openLookup = () => {
    setLookupMode(true);
    goToStep(
      "lookup",
      buildSteps({ alreadyHoldsSlots, returningUser, lookingUp: true })
    );
  };

  const closeLookup = () => {
    setLookupMode(false);
    setHydrateError(null);
    goToStep(
      "welcome",
      buildSteps({ alreadyHoldsSlots, returningUser, lookingUp: false })
    );
  };

  const restart = () => resetSession("welcome");
  const startNewRegistration = () =>
    resetSession(meetingSteps.some((s) => s.key === "pick") ? "pick" : "info");

  async function onLookupSelect(picked: LookupResult) {
    setHydrateError(null);
    try {
      const hydrated = await hydrateRsvp(
        bootstrap.hydrateUrl,
        picked.lookupToken
      );
      setForm({
        first: hydrated.first,
        last: hydrated.last,
        email: hydrated.email,
        phone: hydrated.phone,
        address: "",
        zip: "",
        userIdString: hydrated.userIdString ?? "",
        attend: hydrated.attend,
        smsConsent: hydrated.smsConsent === true,
        customFields: hydrated.customFields ?? {},
        interpreterRequested: false,
        interpreterLanguage: "",
        editToken: hydrated.editToken ?? ""
      });
      const sel: Record<number, Stance> = {};
      Object.keys(hydrated.dispositions).forEach((k) => {
        const n = parseInt(k, 10);
        if (!isNaN(n)) sel[n] = hydrated.dispositions[k];
      });
      setSelections(sel);
      // Bug #10369: hydration replaces this session's selection state wholesale, so the
      // withdrawal ledger has to go with it. Without this, anything the resident deselected
      // before backing out to Lookup would still be submitted as a removal against a
      // registration they had not even loaded yet.
      setRemovedItemIds([]);
      const holdsSlots = Object.keys(sel).length > 0;
      setAlreadyHoldsSlots(holdsSlots);
      const hydratedLanguage = matchKioskLanguage(
        hydrated.languageCode,
        kioskLanguages
      );
      if (hydratedLanguage && !userPickedLanguageRef.current) {
        setLanguage(hydratedLanguage);
        if (hydratedLanguage !== languageCode) {
          applyTranslateLanguage(hydratedLanguage);
        }
      }
      setReturningUser(true);
      setLookupMode(false);
      goToStep(
        "review",
        buildSteps({
          alreadyHoldsSlots: holdsSlots,
          returningUser: true,
          lookingUp: false
        })
      );
    } catch (e: unknown) {
      setHydrateError(e instanceof Error ? e.message : String(e));
    }
  }

  async function submit() {
    setSubmitting(true);
    setSubmitRefusal(null);
    try {
      const dispositions: Record<string, Stance> = {};
      Object.keys(selections).forEach((k) => {
        dispositions[k] = selections[Number(k)];
      });
      const trimmedPhone = form.phone.trim();
      const trimmedEmail = form.email.trim();
      const effectiveSmsConsent =
        (data?.showSmsConsentOption ?? false) &&
        trimmedPhone.length > 0 &&
        form.smsConsent;
      const effectiveSubscribe =
        (data?.showEmailSubscribeOption ?? false) &&
        trimmedEmail.length > 0 &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail) &&
        subscribe;
      const res = await submitCheckIn(bootstrap.submitUrl, {
        first: form.first.trim(),
        last: form.last.trim(),
        email: trimmedEmail,
        phone: trimmedPhone,
        userIdString: form.userIdString ?? "",
        attend: form.attend,
        smsConsent: effectiveSmsConsent,
        subscribe: effectiveSubscribe,
        languageCode,
        dispositions,
        // Bug #10369: the server clears exactly these rows and nothing else, so removing your
        // last item withdraws you without an empty `dispositions` having to mean "delete
        // everything" - which it also means on a fresh check-in the server duplicate-matches.
        removedItemIds,
        customFields: form.customFields ?? {},
        interpreterRequested: form.interpreterRequested || undefined,
        interpreterLanguage: form.interpreterRequested
          ? form.interpreterLanguage
          : undefined,
        editToken: form.editToken || undefined
      });
      applyServerCapacity(res.meeting);
      setDroppedItems(res.droppedItems ?? []);
      if (res.droppedItems && res.droppedItems.length > 0) {
        const droppedIds = new Set(res.droppedItems.map((d) => d.id));
        setSelections((s) => {
          const next: Record<number, Stance> = {};
          Object.keys(s).forEach((k) => {
            const id = Number(k);
            if (!droppedIds.has(id)) next[id] = s[id];
          });
          return next;
        });
      }
      setDone(true);
    } catch (err: unknown) {
      const apiError = err instanceof ApiResponseError ? err : null;
      setSubmitRefusal(
        submitRefusalMessage({
          code: apiError?.code,
          isPolicyRefusal: apiError?.isPolicyRefusal
        })
      );
      setCapacityRefresh((n) => n + 1);
    } finally {
      setSubmitting(false);
    }
  }

  // --- error / loading shells ----------------------------------------------

  if (loadError) {
    return (
      <div className="meeting-kiosk-shell">
        <div className="kiosk">
          <div className="stage">
            <div className="screen active">
              <div className="error">
                <h2>Something went wrong loading the meeting.</h2>
                <p>{loadError}</p>
                <p>Please tell the meeting clerk.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="meeting-kiosk-shell">
        <div className="kiosk">
          <div className="stage">
            <div className="screen active">
              <div className="kiosk-loading">
                <div className="kiosk-loading-spinner" aria-hidden="true">
                  <div className="kiosk-loading-dot" />
                  <div className="kiosk-loading-dot" />
                  <div className="kiosk-loading-dot" />
                </div>
                <p className="kiosk-loading-text">Loading the meeting…</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- main shell ----------------------------------------------------------

  const fmtClock = clock.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit"
  });
  const fmtDate = clock.toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric"
  });
  const selectedCount = Object.keys(selections).length;
  const sealText = (bootstrap.boardName || "Board").slice(0, 3).toUpperCase();
  const customerLabel = normalizeShoutyCaps(
    data.customerName || bootstrap.customerName || ""
  );
  const boardLabel = normalizeShoutyCaps(
    bootstrap.boardName || data.project.name
  );
  const orgText = `${customerLabel} · ${boardLabel}`
    .trim()
    .replace(/^·\s*/, "");

  return (
    <div className="meeting-kiosk-shell">
      <div
        className="kiosk"
        data-screen-label={done ? "Done" : steps[stepIdx]?.label ?? "Welcome"}
      >
        <div className="topbar">
          <div className="agency">
            {bootstrap.customerLogoUrl ? (
              <img
                className="seal-img"
                src={bootstrap.customerLogoUrl}
                alt={`${
                  data.customerName || bootstrap.customerName || "Agency"
                } seal`}
                loading="eager"
                {...({ fetchpriority: "high" } as Record<string, string>)}
              />
            ) : (
              <div className="seal" aria-hidden="true">
                {sealText}
              </div>
            )}
            <div className="text">
              <div className="org">{orgText}</div>
              <div className="meta">Meeting check-in kiosk</div>
            </div>
          </div>
          <div className="clock">
            <Num>{fmtClock}</Num>
            <span className="date">{fmtDate}</span>
          </div>
        </div>

        {!done && (
          <div className="progress">
            <p className="sr-only" aria-live="polite">
              Step {stepIdx + 1} of {steps.length}: {steps[stepIdx]?.label}
            </p>
            <div className="steps" role="list">
              {steps.map((s, i) => (
                <div
                  key={s.key}
                  className={`step ${i < stepIdx ? "done" : ""} ${
                    i === stepIdx ? "active" : ""
                  }`}
                  role="listitem"
                  aria-current={i === stepIdx ? "step" : undefined}
                >
                  <span className="num" aria-hidden="true">
                    <Num>{i + 1}</Num>
                  </span>
                  <span className="step-label">{s.label}</span>
                </div>
              ))}
            </div>
            <button
              className="help"
              type="button"
              onClick={() => setShowHelp(true)}
            >
              <Icon name="help" size={18} /> Get help
            </button>
            <LanguageButton
              languages={kioskLanguages}
              languageCode={languageCode}
              onOpen={() => setShowLangs(true)}
            />
          </div>
        )}

        {!done && closedNotice && (
          <div className="kiosk-notice" role="status" aria-live="polite">
            <div className="kiosk-notice-body">
              <Icon name="help" size={20} />
              <span>{closedNotice}</span>
            </div>
            <button
              type="button"
              className="kiosk-notice-close"
              onClick={() => setClosedNotice(null)}
              aria-label="Dismiss notice"
            >
              ×
            </button>
          </div>
        )}

        {done && kioskLanguages.length > 1 && (
          <div className="done-translate-bar">
            <LanguageButton
              languages={kioskLanguages}
              languageCode={languageCode}
              onOpen={() => setShowLangs(true)}
            />
          </div>
        )}

        <div className="stage">
          <span
            key={`translate-probe-${done ? "done" : screen}`}
            className="sr-only translate-probe"
            aria-hidden="true"
          >
            {TRANSLATE_PROBE_TEXT}
          </span>
          {done ? (
            <div className="screen active">
              <Done
                form={form}
                selections={selections}
                agendaItems={data.agendaItems}
                sections={data.sections}
                hasPickStep={hasPickStep}
                onRestart={restart}
                droppedItems={droppedItems}
                confirmationContent={bootstrap.confirmationContent}
              />
            </div>
          ) : showLookupScreen ? (
            <div className="screen active">
              <Lookup
                lookupUrl={bootstrap.lookupUrl}
                onCancel={closeLookup}
                onStartNew={startNewRegistration}
                onSelect={onLookupSelect}
              />
              {hydrateError && (
                <div
                  className="submit-error"
                  role="alert"
                  style={{ marginTop: 12 }}
                >
                  Couldn't load that registration: {hydrateError}
                </div>
              )}
            </div>
          ) : screen === "welcome" ? (
            <div className="screen active">
              <Welcome
                data={data}
                openingContent={bootstrap.openingContent}
                onStart={startNewRegistration}
                onLookup={openLookup}
                speakerSignupAvailable={hasPickStep}
                languages={kioskLanguages}
                languageCode={languageCode}
                onSelectLanguage={selectLanguage}
              />
            </div>
          ) : screen === "info" ? (
            <div className="screen active">
              <YourInfo
                form={form}
                setForm={setForm}
                subscribe={subscribe}
                setSubscribe={setSubscribe}
                registrationFields={registrationFields}
                onShowSmsInfo={() => setShowSmsInfo(true)}
                interpreterServicesEnabled={
                  data.inPersonTranslationServices ?? false
                }
                interpreterLanguages={data.inPersonSupportedLanguages ?? []}
                showEmailSubscribeOption={
                  data.showEmailSubscribeOption ?? false
                }
                subscribePrompt={data.subscribePrompt}
                showSmsConsentOption={data.showSmsConsentOption ?? false}
                fieldMessages={infoValidation.messages}
                speakerRegistrationEnabled={
                  data.meeting?.speakersCanRegister === true
                }
              />
            </div>
          ) : screen === "pick" ? (
            <div className="screen active">
              <Picker
                agendaItems={data.agendaItems}
                sections={data.sections ?? EMPTY_SECTIONS}
                selections={selections}
                setSelections={setSelections}
                onUserToggledItem={onUserToggledItem}
                collectDisposition={data.collectDisposition !== false}
                dispositionOptions={data.speakerDispositionOptions}
                maxItemsPerSpeaker={data.maxAgendaItemsPerSpeaker ?? undefined}
              />
            </div>
          ) : (
            <div className="screen active">
              <Review
                form={form}
                selections={selections}
                agendaItems={data.agendaItems}
                sections={data.sections}
                returningUser={returningUser}
                collectDisposition={data.collectDisposition !== false}
                hasPickStep={hasPickStep}
                onEditPicks={() => goToStep("pick")}
                onEditDetails={() => goToStep("info")}
              />
            </div>
          )}
        </div>

        {!done && !showLookupScreen && stepIdx > 0 && (
          <div className="actionbar">
            <div>
              <button
                className="btn"
                type="button"
                onClick={back}
                disabled={submitting}
              >
                <Icon name="arrow-left" size={20} /> Back
              </button>
            </div>
            {screen === "pick" && (
              <div className="summary">
                <Num className="count">{selectedCount}</Num>{" "}
                {data.maxAgendaItemsPerSpeaker
                  ? `of ${data.maxAgendaItemsPerSpeaker} ${
                      data.maxAgendaItemsPerSpeaker === 1 ? "item" : "items"
                    } selected`
                  : `${selectedCount === 1 ? "item" : "items"} selected`}
                <span className="sub">
                  {data.maxAgendaItemsPerSpeaker != null &&
                  selectedCount >= data.maxAgendaItemsPerSpeaker
                    ? "You've reached the item limit. Remove one to choose another."
                    : "You can also check in without speaking on any items."}
                </span>
              </div>
            )}
            <div className="actions">
              {submitRefusal && (
                <span className="submit-error" role="alert">
                  <Icon name="help" size={20} />
                  {submitRefusal.message}
                </span>
              )}
              {screen === "info" && infoValidation.blockingReason && (
                <span
                  className="advance-blocked-reason"
                  id="advance-blocked-reason"
                  role="status"
                  aria-live="polite"
                >
                  {infoValidation.blockingReason}
                </span>
              )}
              {submitRefusal?.isTerminal ? (
                <button
                  className="btn lg primary"
                  type="button"
                  onClick={restart}
                >
                  Start over <Icon name="arrow-right" size={22} />
                </button>
              ) : (
                <button
                  className="btn lg primary"
                  type="button"
                  disabled={!canAdvance || submitting}
                  aria-describedby={
                    screen === "info" && infoValidation.blockingReason
                      ? "advance-blocked-reason"
                      : undefined
                  }
                  onClick={next}
                >
                  {isFinalStep
                    ? submitting
                      ? "Submitting…"
                      : "Confirm check-in"
                    : "Continue"}{" "}
                  <Icon name="arrow-right" size={22} />
                </button>
              )}
            </div>
          </div>
        )}

        {showLangs && kioskLanguages.length > 1 && (
          <Sheet
            title="Choose a language"
            onClose={() => setShowLangs(false)}
            className="langs-sheet"
            centered
          >
            <p className="sheet-sub">Tap a language to translate the page.</p>
            <div className="langs-grid notranslate" translate="no">
              {kioskLanguages.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  className={`lang-row ${
                    languageCode === lang.code ? "on" : ""
                  }`}
                  onClick={() => {
                    selectLanguage(lang.code);
                    setShowLangs(false);
                  }}
                  aria-pressed={languageCode === lang.code}
                >
                  <span className="lang-native">
                    {lang.autoglossonym || lang.name || lang.code}
                  </span>
                  {lang.name && lang.autoglossonym !== lang.name && (
                    <span className="lang-english">{lang.name}</span>
                  )}
                </button>
              ))}
            </div>
          </Sheet>
        )}

        {showHelp && (
          <Sheet
            title="Need help?"
            onClose={() => setShowHelp(false)}
            className="info-sheet"
          >
            <div className="info-body">
              {/* AB#9994: "the front desk" is not a place in every venue — LA
                  County's Board Chambers has no front desk, so the wording sent
                  residents looking for something that doesn't exist. The lead
                  clause is now agency-neutral by default and overridable per
                  kiosk (KioskMode.HelpContent).

                  The trailing "for sign-in help, …" is deliberately left static
                  so an override composes into a grammatical sentence rather than
                  replacing the whole paragraph: "Ask the clerk at the dais for
                  sign-in help, …".

                  Plain text rendered as a JSX text node, never via
                  dangerouslySetInnerHTML, so no sanitizer step is required. */}
              <p>
                <strong>{resolveHelpLead(bootstrap.helpContent)}</strong> for
                sign-in help, language assistance, or questions about the agenda
                items.
              </p>
              <p>
                <strong>Need to attend remotely?</strong> Chat with staff — this
                kiosk is for in-person sign-ups only.
              </p>
              <ul>
                <li>
                  Tap <strong>Back</strong> on any screen to make changes.
                </li>
                <li>
                  Only items where you tap a stance are added to the speaker
                  list.
                </li>
              </ul>
              <p className="link-row">
                Kiosk powered by PublicInput. Technical support:{" "}
                <a href="mailto:support@publicinput.com">
                  support@publicinput.com
                </a>
                .
              </p>
            </div>
            <div className="info-actions">
              <button
                className="btn primary"
                type="button"
                onClick={() => setShowHelp(false)}
              >
                Got it
              </button>
            </div>
          </Sheet>
        )}

        {showSmsInfo && (
          <Sheet
            title="About text messages"
            onClose={() => setShowSmsInfo(false)}
            className="info-sheet"
          >
            <div className="info-body">
              <p>
                By opting in, you agree to receive automated text messages from
                the meeting organizer at the number provided, regarding your
                meeting check-in, reminders, and follow-ups. Consent is not a
                condition of participation.
              </p>
              <ul>
                <li>
                  <strong>Message frequency varies.</strong> Typically 1–4
                  messages per meeting.
                </li>
                <li>
                  <strong>Message and data rates may apply.</strong> Standard
                  carrier charges apply.
                </li>
                <li>
                  Reply <strong>STOP</strong> at any time to opt out, or{" "}
                  <strong>HELP</strong> for help.
                </li>
              </ul>
              <p className="link-row">
                Read our full privacy policy at{" "}
                <a
                  href="https://publicinput.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  publicinput.com/privacy
                </a>
                .
              </p>
            </div>
            <div className="info-actions">
              <button
                className="btn primary"
                type="button"
                onClick={() => setShowSmsInfo(false)}
              >
                Got it
              </button>
            </div>
          </Sheet>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sheet — accessible modal wrapper
// ---------------------------------------------------------------------------

interface SheetProps {
  title: string;
  onClose: () => void;
  className?: string;
  centered?: boolean;
  children: React.ReactNode;
}

const Sheet: React.FC<SheetProps> = ({
  title,
  onClose,
  className = "",
  centered = false,
  children
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;
    dialog?.focus();

    const focusables = (): HTMLElement[] =>
      dialog
        ? Array.from(
            dialog.querySelectorAll<HTMLElement>(
              'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
            )
          ).filter((el) => el.offsetParent !== null)
        : [];

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const items = focusables();
      if (items.length === 0) {
        e.preventDefault();
        dialog?.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === dialog)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      previouslyFocused?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={`sheet-scrim${centered ? " sheet-scrim-centered" : ""}`}
      onClick={() => onClose()}
    >
      <div
        ref={dialogRef}
        className={`sheet ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet-head">
          <h3 id={titleId}>{title}</h3>
          <button
            className="sheet-close"
            type="button"
            onClick={() => onClose()}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// translation helpers (App-scoped)
// ---------------------------------------------------------------------------

function matchKioskLanguage(
  code: string | null | undefined,
  langs: KioskLanguage[]
): string | null {
  const trimmed = (code ?? "").trim().toLowerCase();
  if (!trimmed) return null;
  const match = langs.find((l) => l.code.toLowerCase() === trimmed);
  return match ? match.code : null;
}

function googTransCookieTargets(): string[] {
  if (typeof document === "undefined") return [];
  const targets: string[] = [];
  document.cookie.split(";").forEach((part) => {
    const eq = part.indexOf("=");
    if (eq < 0) return;
    if (part.slice(0, eq).trim() !== "googtrans") return;
    let value = part.slice(eq + 1).trim();
    try {
      value = decodeURIComponent(value);
    } catch {
      /* malformed */
    }
    const segments = value.split("/").filter((s) => s.length > 0);
    const target =
      segments.length > 0 ? segments[segments.length - 1].toLowerCase() : "";
    if (target && target !== "en" && !targets.includes(target)) {
      targets.push(target);
    }
  });
  return targets;
}
